import { cord, formatInput, gql2 } from "@/gql/lib";
import { Subject, InsightCounter, InfoTypeSummeryItem } from "./model";
import {
  ActionEnum,
  AxisType,
  CountParametersTier1Tier2,
  targetAxis,
} from "@/gql/types";
import {
  InfoTypeHelper,
  InformationType,
  POPULATION_TYPE,
  PopulationTypeHelper,
} from "@/common/models";
import { SharedState } from "@/common/state";
import { path, pipe2 } from "@/fp";
import { apiCall } from "@/common/api/ApiResult.model";
import axios from "axios";

export const get_insights_for_entity = gql2<Subject, InsightCounter[]>(
  "investigation.summary.insights",
  (subject, { frame }) => {
    const entity_type =
      subject.type == POPULATION_TYPE.EMPLOYEE
        ? AxisType.Employee
        : PopulationTypeHelper.asAxisType2(subject.type, "sender");
    const countQuery: CountParametersTier1Tier2 = {
      coordinate: {
        x: cord(entity_type, [subject.id]),
        y: cord(AxisType.Insight, []),
        z: frame,
      },
      page: 0,
      pageSize: 100,
      action: ActionEnum.List,
      targetAxis: targetAxis.Y,
    };
    return {
      query: `
        {
            result :tier2count(params:${formatInput(countQuery)}){
            count
            insights {
                id
                name
                severity
                description
            }
            }
        }
     `,
      mapper: (res) => {
        return res.result
          .map((item: any) => {
            return {
              ...item.insights[0],
              count: item.count,
            };
          })
          .filter((item: any) => item.count > 0 && item.severity > 0);
      },
    };
  }
);

const filter_for_content_labels = (item: { y: { value: string } }) => {
  const label = SharedState.getInfoType(item.y.value);
  return (
    label &&
    SharedState.is_content_label(label.id) &&
    label.id !== SharedState.default_label_id.value
  );
};

/* pipe2(
  path("y.value", () => ""),
  SharedState.is_content_label
);
 */
export const get_info_type_counters_for_entity = gql2<
  Subject,
  InfoTypeSummeryItem[]
>("investigation.summary.infoTypes", (subject, timeFrames) => {
  let countQuery: CountParametersTier1Tier2;
  if (subject.type === POPULATION_TYPE.LABEL) {
    countQuery = {
      coordinate: {
        x: cord(AxisType.None),
        y: cord(AxisType.ContentLabel, []),
        z: timeFrames.frame,
      },
      page: 0,
      pageSize: 1000,
      action: ActionEnum.List,
      targetAxis: targetAxis.X,
    };
  } else {
    countQuery = {
      coordinate: {
        x: cord(PopulationTypeHelper.asAxisType2(subject.type, "both"), [
          subject.id,
        ]),
        y: cord(AxisType.ContentLabel, []),
        z: timeFrames.frame,
      },
      page: 0,
      pageSize: 1000,
      action: ActionEnum.List,
      targetAxis: targetAxis.Y,
    };
  }

  return {
    query: `
    {
      infoTypes :tier2count(params:${formatInput(countQuery)}){
        count,
        y{value}
      }
    }
    `,
    mapper: (res) => {
      const { typesByCategories, l0InfoTypes, allInfoTypes } = SharedState;
      const l0s = l0InfoTypes.value.filter(
        (d) => d.id !== SharedState.default_label_id.value
      );
      // just content labels
      // and the counts for each
      if (subject.type === POPULATION_TYPE.LABEL) {
        const subject_label_code = SharedState.getInfoType(subject.id).code;
        res.infoTypes = res.infoTypes.filter((d: any) => {
          const label_id = d.y.value;
          return (
            SharedState.is_content_label(label_id) &&
            SharedState.getInfoType(label_id).code == subject_label_code
          );
        });
      }

      const allInfoTypesCounts: { [key: string]: number } = res.infoTypes
        .filter(filter_for_content_labels)
        .map((item: any) => [item.y.value, item.count])
        // @ts-ignore
        .reduce((acc, item) => {
          const [infoTypeId, count] = item;
          acc[infoTypeId] = count;
          return acc;
        }, {} as { [key: string]: number });

      const response: InfoTypeSummeryItem[] = [];

      const l0IdsAndCodes = l0s
        .filter((d) => {
          if (subject.type === POPULATION_TYPE.LABEL) {
            return d.code === SharedState.allInfoTypes.value[subject.id].code;
          }
          return true;
        })
        .map((d) => ({
          id: d.id,
          code: d.code,
        }));

      const l0_total_count = l0IdsAndCodes.reduce((acc, { id }) => {
        acc += allInfoTypesCounts[id] || 0;
        return acc;
      }, 0);

      try {
        for (const { id: l0_id, code } of l0IdsAndCodes.filter(({ id: d }) =>
          typesByCategories.value.has(d)
        )) {
          const color = InfoTypeHelper.colorByL0Code(code);
          const l0Type = allInfoTypes.value[l0_id];
          const items = [
            ...(typesByCategories.value.get(l0_id) as InformationType[])
              //.filter(d => d.generation == 1)
              .map((t) => ({
                id: t.id,
                name: t.name || t.labelName || t.shortName,
                count: allInfoTypesCounts[t.id] || 0,
                generation: t.generation,
                num: allInfoTypesCounts[t.id],
                items: [],
                color,
              })),
          ].filter((f) => f.count);
          //
          const l1_items_total = items.reduce((acc, item) => {
            acc += item.count;
            return acc;
          }, 0);

          const l0CountPercentage = +(
            (allInfoTypesCounts[l0_id] / l0_total_count) *
            100
          ).toFixed(1);

          response.push({
            id: l0_id,
            name:
              l0Type.name || l0Type.labelName || (l0Type.shortName as string),
            count: l0CountPercentage || 0,
            // @ts-ignore
            num: allInfoTypesCounts[l0_id],
            generation: 0,
            items: items
              .map((L1) => {
                const tmp = L1.count;
                L1.count = +((L1.count / l1_items_total) * 100).toFixed(1);
                // @ts-ignore
                const L1_tree_path = SharedState.allInfoTypes.value[L1.id].path;
                const L1_in_tree = SharedState.info_types_tree.value
                  .find((l) => l.id === L1_tree_path[0])
                  ?.nested.find((l) => l.id === L1_tree_path[1]);
                if (L1_in_tree && L1_in_tree.nested) {
                  // @ts-ignore
                  L1.items = L1_in_tree.nested
                    .filter((l) => allInfoTypesCounts[l.id])
                    .map((L2) => {
                      return {
                        id: L2.id,
                        name: L2.labelName,
                        generation: 2,
                        num: allInfoTypesCounts[L2.id],
                        count: +(
                          (allInfoTypesCounts[L2.id] / tmp) *
                          100
                        ).toFixed(1),
                        items: [],
                        color: L1.color,
                      };
                    });
                } else {
                  // @ts-ignore
                  L1.items = [];
                }
                return L1;
              })
              .sort((a, b) => b.count - a.count),
            color,
          });
        }
        return response;
      } catch (e) {
        console.log("summary api info types e : ", e);
        return [];
      }
    },
  };
});

export const set_cluster_label = apiCall<
  { label_id: string; cluster_id: string },
  boolean
>("cluster.set-label", async ({ cluster_id, label_id }) => {
  await axios.put(`/label-api/cluster/${cluster_id}/label/${label_id}`);
  return {
    success: true,
    value: true,
  };
});

export const get_cluster_label = apiCall<string, string>(
  "cluster.get-label",
  async (cluster_id) => {
    const res = await axios.get(
      `/label-api/cluster/${cluster_id}?resolve=false`
    );
    return {
      success: true,
      value: res.data.label || "",
    };
  }
);

export const remove_cluster_label = apiCall<string, string>(
  "cluster.remove-label",
  async (cluster_id) => {
    const res = await axios.delete(`/label-api/cluster/${cluster_id}`);
    return {
      success: true,
      value: res.data.label || "",
    };
  }
);
